import { useCallback, type InputHTMLAttributes } from 'react';
import { formatPhone, sanitizePhone } from '@/lib/validators';

export interface PhoneInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value' | 'type'> {
  /** Raw 10-digit value (no dashes). The component formats it for display. */
  value: string;
  /** Called with raw digits only (e.g. '1234567890'). */
  onValueChange: (digits: string) => void;
}

/**
 * Reusable masked phone input that displays `123-456-7890` while
 * keeping the underlying value as raw digits for backend/state.
 *
 * Usage:
 * ```tsx
 * <PhoneInput
 *   value={phone}
 *   onValueChange={(digits) => setPhone(digits)}
 *   className={inputNormal}
 *   placeholder="123-456-7890"
 * />
 * ```
 */
export function PhoneInput({ value, onValueChange, ...rest }: PhoneInputProps) {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onValueChange(sanitizePhone(e.target.value));
    },
    [onValueChange],
  );

  return (
    <input
      type="tel"
      inputMode="numeric"
      maxLength={12} /* 10 digits + 2 dashes */
      autoComplete="tel-national"
      {...rest}
      value={formatPhone(value)}
      onChange={handleChange}
    />
  );
}
